<template>
  <div class="mx-auto flex w-full flex-col gap-4">
    <p v-if="loadErrorMessage" class="text-sm text-destructive">
      {{ loadErrorMessage }}
    </p>

    <div v-if="loadingChecklistTemplate" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
      Loading checklist template...
    </div>

    <p v-else-if="!checklistTemplate" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
      Checklist template not found.
    </p>

    <ChecklistTemplate
      v-else
      :checklist-template="checklistTemplate"
      :initially-minimized="false"
      :initially-editing="shouldStartEditing"
      :show-manage-actions="true"
      edit-in-place
      @published="handleChecklistTemplatePublished"
      @aborted="handleChecklistTemplateAborted"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ChecklistTemplate from '../components/checklist-templates/ChecklistTemplate.vue'
import { useChecklistTemplatesStore } from '../stores/checklistTemplatesStore.js'

const route = useRoute()
const router = useRouter()
const checklistTemplatesStore = useChecklistTemplatesStore('show-page')

const checklistTemplate = ref(null)
const loadingChecklistTemplate = ref(false)
const loadErrorMessage = ref(null)

const checklistTemplateId = computed(() => Number(route.params.checklistTemplateId ?? 0))
const businessSystemId = computed(() => Number(route.params.businessSystem ?? 0))
const isValidChecklistTemplateId = computed(() => Number.isInteger(checklistTemplateId.value) && checklistTemplateId.value > 0)
const isBusinessDocumentationSystemRoute = computed(() => {
  return route.name === 'BdsChecklistTemplateShowPage' || route.name === 'BdsChecklistTemplateEditPage'
})
const isEditRoute = computed(() => {
  return route.name === 'ChecklistTemplateEditPage' || route.name === 'BdsChecklistTemplateEditPage'
})
const shouldStartEditing = computed(() => {
  return isEditRoute.value && checklistTemplate.value?.status === 'draft'
})

const resolveShowRoute = (nextChecklistTemplateId) => {
  const normalizedChecklistTemplateId = Number(nextChecklistTemplateId)

  if (!Number.isInteger(normalizedChecklistTemplateId) || normalizedChecklistTemplateId < 1) {
    return null
  }

  if (isBusinessDocumentationSystemRoute.value) {
    return {
      name: 'BdsChecklistTemplateShowPage',
      params: {
        businessSystem: businessSystemId.value,
        checklistTemplateId: normalizedChecklistTemplateId,
      },
    }
  }

  return {
    name: 'ChecklistTemplateShowPage',
    params: {
      checklistTemplateId: normalizedChecklistTemplateId,
    },
  }
}

const resolveIndexRoute = () => {
  if (isBusinessDocumentationSystemRoute.value) {
    return {
      name: 'ChecklistTemplatesShowPage',
      params: {
        businessSystem: businessSystemId.value,
      },
    }
  }

  return {
    name: 'ChecklistTemplatesIndexPage',
  }
}

const fetchChecklistTemplate = async () => {
  if (!isValidChecklistTemplateId.value) {
    checklistTemplate.value = null
    loadErrorMessage.value = 'Invalid checklist template id.'
    return
  }

  loadingChecklistTemplate.value = true
  loadErrorMessage.value = null

  try {
    const fetchedChecklistTemplate = await checklistTemplatesStore.fetchItemById(checklistTemplateId.value)

    if (!fetchedChecklistTemplate) {
      checklistTemplate.value = null
      return
    }

    checklistTemplate.value = fetchedChecklistTemplate
  } catch (error) {
    checklistTemplate.value = null
    loadErrorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to load checklist template.'
  } finally {
    loadingChecklistTemplate.value = false
  }
}

const replaceChecklistTemplateRoute = async (nextChecklistTemplateId) => {
  const targetRoute = resolveShowRoute(nextChecklistTemplateId)

  if (!targetRoute) {
    return
  }

  await router.replace(targetRoute)
}

const handleChecklistTemplatePublished = async (publishedChecklistTemplate) => {
  if (!publishedChecklistTemplate) {
    return
  }

  checklistTemplate.value = publishedChecklistTemplate

  if (isEditRoute.value || Number(publishedChecklistTemplate.id) !== checklistTemplateId.value) {
    await replaceChecklistTemplateRoute(publishedChecklistTemplate.id)
  }
}

const handleChecklistTemplateAborted = async (abortPayload) => {
  const originalChecklistTemplate = abortPayload?.original_checklist_template ?? null

  if (originalChecklistTemplate) {
    checklistTemplate.value = originalChecklistTemplate

    if (isEditRoute.value || Number(originalChecklistTemplate.id) !== checklistTemplateId.value) {
      await replaceChecklistTemplateRoute(originalChecklistTemplate.id)
    }

    return
  }

  checklistTemplate.value = null
  await router.push(resolveIndexRoute())
}

watch(checklistTemplateId, async () => {
  await fetchChecklistTemplate()
}, { immediate: true })
</script>
